import React,{useState,useRef,useCallback,useEffect} from 'react';
import {AdminApp} from './apps/AdminApp';
import {AuthScreen,GoogleOnboarding,NewOrgScreen,ResetPasswordScreen} from './apps/AuthScreens';
import {MainApp} from './apps/MainApp';
import {ParentApp} from './apps/ParentApp';
import {PlayerApp} from './apps/PlayerApp';
import {api} from './lib/api';
import {PENDING_REG_KEY,SESSION_KEY,TERMS_VERSION} from './lib/constants';

export function App(){
  const [auth,setAuth]=useState(null);const [needsOrg,setNeedsOrg]=useState(false);
  const [oauth,setOauth]=useState({checking:true,user:null,token:null,error:""});
  const [recovery,setRecovery]=useState(null); // {token} while on the password-reset screen
  // Whether we're still trying to restore a previously logged-in session from
  // localStorage (see the effect below) — kept separate from oauth.checking so
  // the two async startup checks (an OAuth redirect vs. a returning session)
  // can run at the same time without either one flashing the login screen
  // before it's actually known whether the person is already signed in.
  const [restoringSession,setRestoringSession]=useState(true);

  // Without this, every full page reload signs the person straight out — including
  // completely ordinary ones like returning from a Stripe Checkout payment, which
  // navigates away from the app and back via a real browser redirect, not a
  // same-page navigation. `auth` used to live only in React state, so that round
  // trip wiped it and dropped them back on the login screen. Persist it to
  // localStorage on every sign-in, and try to restore + revalidate it here before
  // ever showing the login screen.
  useEffect(()=>{
    async function restoreSession(){
      let saved=null;
      try{ const raw=localStorage.getItem(SESSION_KEY); if(raw)saved=JSON.parse(raw); }catch(e){}
      if(!saved||!saved.token){ setRestoringSession(false); return; }
      const user=await api.getUser(saved.token);
      if(!user||!user.id){
        try{ localStorage.removeItem(SESSION_KEY); }catch(e){}
        setRestoringSession(false);
        return;
      }
      setAuth(saved);
      // Same needsOrg gap as handleOAuthRedirect had: a session saved mid-signup
      // (org created but the season/billing steps not finished, or persisted via
      // handleAuth with newOrg:true) needs this checked here too, or refreshing
      // the page mid-wizard would drop them onto MainApp with no organisation
      // instead of resuming "Set Up Your Organisation".
      if(saved.newOrg)setNeedsOrg(true);
      setRestoringSession(false);
    }
    restoreSession();
  },[]);

  useEffect(()=>{
    async function handleOAuthRedirect(){
      const hash=window.location.hash;
      if(!hash||!hash.includes("access_token")){ setOauth(s=>({...s,checking:false})); return; }
      const params=new URLSearchParams(hash.substring(1));
      const access_token=params.get("access_token");
      const type=params.get("type");
      const errorDesc=params.get("error_description")||params.get("error");
      window.history.replaceState(null,"",window.location.pathname+window.location.search);
      if(errorDesc){ setOauth({checking:false,user:null,token:null,error:errorDesc}); return; }
      if(!access_token){ setOauth(s=>({...s,checking:false})); return; }
      if(type==="recovery"){ setRecovery({token:access_token}); setOauth(s=>({...s,checking:false})); return; }
      const gUser=await api.getUser(access_token);
      if(!gUser||!gUser.id){ setOauth({checking:false,user:null,token:null,error:"Google sign-in failed. Please try again."}); return; }
      const admins=await api.get(access_token,"platform_admins",`id=eq.${gUser.id}`);
      if(admins&&admins.length>0){
        handleAuth({token:access_token,user:gUser,admin:admins[0],accountType:"admin"}); return;
      }
      const coaches=await api.get(access_token,"coaches",`id=eq.${gUser.id}`);
      if(coaches&&coaches.length>0){
        const oc=await api.get(access_token,"org_coaches",`coach_id=eq.${gUser.id}&select=id`);
        const hasOrg=oc&&oc.length>0;
        if(!hasOrg&&coaches[0].role==="head"){
          // Head-coach account exists but they never finished creating their organisation — send them to finish that step.
          handleAuth({token:access_token,user:gUser,coach:coaches[0],accountType:"coach",newOrg:true});
          return;
        }
        if(!hasOrg){
          setOauth({checking:false,user:null,token:null,error:"This account isn't linked to an organisation yet. If you haven't registered, please use the Register tab above. If a head coach invited you, double-check your join code."});
          return;
        }
        handleAuth({token:access_token,user:gUser,coach:coaches[0],accountType:"coach"}); return;
      }
      const viewers=await api.get(access_token,"team_viewers",`id=eq.${gUser.id}`);
      if(viewers&&viewers.length>0){
        const v=viewers[0];
        const nowIso=new Date().toISOString();
        api.patch(access_token,"team_viewers",v.id,{login_count:(v.login_count||0)+1,last_login_at:nowIso}); // fire-and-forget
        handleAuth({token:access_token,user:gUser,viewer:{...v,login_count:(v.login_count||0)+1,last_login_at:nowIso},accountType:"viewer"}); return;
      }

      // Brand-new account. If this looks like a return from an email-confirmation link
      // (rather than Google), we may have remembered what they were signing up for —
      // finish setting up their account automatically instead of asking again.
      let pending=null;
      try{ const raw=localStorage.getItem(PENDING_REG_KEY); if(raw)pending=JSON.parse(raw); }catch(e){}
      if(pending&&pending.email&&gUser.email&&pending.email.toLowerCase()===gUser.email.toLowerCase()){
        try{
          const {role,code,name,termsAcceptedAt}=pending;
          const uid=gUser.id, email=gUser.email;
          const acceptedAt=termsAcceptedAt||new Date().toISOString();
          if(role==="head"){
            await api.post(access_token,"coaches",[{id:uid,name,email,role:"head",terms_accepted_at:acceptedAt,terms_version:TERMS_VERSION}]);
            localStorage.removeItem(PENDING_REG_KEY);
            handleAuth({token:access_token,user:gUser,coach:{id:uid,name,email,role:"head"},accountType:"coach",newOrg:true});
            return;
          } else if(role==="coach"){
            const orgs=await api.get(null,"organisations",`join_code=eq.${(code||"").trim().toUpperCase()}&select=id`);
            if(orgs&&orgs.length>0){
              await api.post(access_token,"coaches",[{id:uid,name,email,role:"assistant",terms_accepted_at:acceptedAt,terms_version:TERMS_VERSION}]);
              await api.post(access_token,"org_coaches",[{org_id:orgs[0].id,coach_id:uid,role:"assistant"}]);
              localStorage.removeItem(PENDING_REG_KEY);
              handleAuth({token:access_token,user:gUser,coach:{id:uid,name,email,role:"assistant"},accountType:"coach"});
              return;
            }
          } else if(role==="player"){
            const teams=await api.get(null,"teams",`player_join_code=eq.${(code||"").trim().toUpperCase()}&select=id,org_id`);
            if(teams&&teams.length>0){
              await api.post(access_token,"team_viewers",[{id:uid,team_id:teams[0].id,org_id:teams[0].org_id,name,email,terms_accepted_at:acceptedAt,terms_version:TERMS_VERSION}]);
              localStorage.removeItem(PENDING_REG_KEY);
              handleAuth({token:access_token,user:gUser,viewer:{id:uid,team_id:teams[0].id,org_id:teams[0].org_id,name,email},accountType:"viewer"});
              return;
            }
          } else if(role==="parent"){
            const kids=await api.get(null,"players",`parent_join_code=eq.${(code||"").trim().toUpperCase()}&select=id,org_id`);
            if(kids&&kids.length>0){
              const teamRows=await api.get(null,"team_players",`player_id=eq.${kids[0].id}&select=team_id`);
              const teamId=teamRows&&teamRows[0]?teamRows[0].team_id:null;
              if(teamId){
                await api.post(access_token,"team_viewers",[{id:uid,team_id:teamId,org_id:kids[0].org_id,name,email,account_role:"parent",player_id:kids[0].id,player_link_approved:true,terms_accepted_at:acceptedAt,terms_version:TERMS_VERSION}]);
                await api.post(access_token,"parent_children",[{parent_viewer_id:uid,player_id:kids[0].id,team_id:teamId,org_id:kids[0].org_id}]);
                localStorage.removeItem(PENDING_REG_KEY);
                handleAuth({token:access_token,user:gUser,viewer:{id:uid,team_id:teamId,org_id:kids[0].org_id,name,email,account_role:"parent",player_id:kids[0].id,player_link_approved:true},accountType:"viewer"});
                return;
              }
            }
          }
        }catch(e){}
        // Fell through — e.g. join code no longer valid. Clear it and let them pick manually below.
        localStorage.removeItem(PENDING_REG_KEY);
      }

      // Brand-new Google sign-in (or a confirm-email return with no usable pending info)
      // — needs to choose head coach / join as coach / join as player
      setOauth({checking:false,user:gUser,token:access_token,error:""});
    }
    handleOAuthRedirect();
  },[]);

  function persistSession(a){ try{ localStorage.setItem(SESSION_KEY,JSON.stringify(a)); }catch(e){} }
  function handleAuth(a){
    setAuth(a);if(a.newOrg)setNeedsOrg(true);persistSession(a);setOauth({checking:false,user:null,token:null,error:""});
    // Signing in from a public marketing page (/about, /pricing, /contact — see
    // AuthScreen's routing) shouldn't leave that URL sitting in the address bar
    // once the authenticated app takes over.
    if(window.location.pathname!=="/")window.history.replaceState({},"","/"+window.location.search);
  }
  function handleSignOut(){if(auth?.token)api.signOut(auth.token);setAuth(null);setNeedsOrg(false);try{ localStorage.removeItem(SESSION_KEY); }catch(e){}}
  function handleOrgCreated(){setNeedsOrg(false);persistSession({...auth,newOrg:false});}
  function handleOAuthCancel(){ if(oauth.token)api.signOut(oauth.token); setOauth({checking:false,user:null,token:null,error:""}); }
  function handleRecoveryDone(){ if(recovery?.token)api.signOut(recovery.token); setRecovery(null); }

  if(oauth.checking||restoringSession)return(<div style={{minHeight:"100vh",background:"var(--c-bg0)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10}}><div style={{fontSize:32}}>⚽</div><div style={{color:"var(--c-muted)",fontSize:13}}>Signing in…</div></div>);
  if(recovery)return <ResetPasswordScreen token={recovery.token} onDone={handleRecoveryDone}/>;
  if(oauth.user)return <GoogleOnboarding token={oauth.token} user={oauth.user} onAuth={handleAuth} onCancel={handleOAuthCancel}/>;
  if(!auth)return <AuthScreen onAuth={handleAuth} oauthError={oauth.error}/>;
  if(auth.accountType==="admin")return <AdminApp auth={auth} onSignOut={handleSignOut}/>;
  if(auth.accountType==="viewer"&&auth.viewer?.account_role==="parent")return <ParentApp auth={auth} onSignOut={handleSignOut}/>;
  if(auth.accountType==="viewer")return <PlayerApp auth={auth} onSignOut={handleSignOut}/>;
  if(needsOrg)return <NewOrgScreen auth={auth} onDone={handleOrgCreated}/>;
  return <MainApp key={needsOrg} auth={auth} onSignOut={handleSignOut}/>;
}

export default App;

